import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { CIBLE_MIN, COULEURS, styleBouton } from '../../../app/styles.js';
import type { ArticleCatalogue, NouveauSupplement, SourceSupplements, SupplementsDuDossier } from './source.js';

/**
 * « Suppléments en un clic » de la facturation du dossier (D08, PARITE-5, démo facturation-ged/Supplements, RG-25). Le catalogue et
 * les montants viennent de l'API (donnée versionnée) ; tant que la grille tarifaire n'est pas reçue, le montant se saisit. Une ligne
 * peut être comptée sans être facturée ; une ligne déjà facturée ne se retire plus. Réservé à l'équipe : rien n'est montré au client.
 */
export interface PropsCarteSupplements {
  source: SourceSupplements;
  peutEcrire: boolean;
}

const RAISON = 'Votre rôle ne permet pas d’ajouter des suppléments.';

/** « 1 234,50 € » (espaces simples). */
export function euros(centimes: number): string {
  const [e, c] = (centimes / 100).toFixed(2).split('.');
  return `${(e ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${c} €`;
}

/** Montant saisi en euros (« 150 », « 150,50 », « 1 234.5 ») vers des centimes entiers positifs ; null si illisible. */
export function centimesDe(saisie: string): number | null {
  const t = saisie.replace(/[\s\u00a0\u202f€]/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const c = Math.round(Number(t) * 100);
  return c > 0 ? c : null;
}

const messageDe = (e: unknown): string =>
  e instanceof ErreurApi
    ? e.code === 'VALIDATION_FAILED' && e.detail
      ? e.detail
      : e.messageUtilisateur
    : 'Une erreur est survenue. Réessayez.';

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13, margin: 0 };
const champ: CSSProperties = {
  font: 'inherit',
  minHeight: CIBLE_MIN,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  width: '100%',
  boxSizing: 'border-box',
};
const badge = (fond: string, encre: string): CSSProperties => ({
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 600,
  background: fond,
  color: encre,
  borderRadius: 999,
  padding: '2px 10px',
});

export function CarteSupplements({ source, peutEcrire }: PropsCarteSupplements) {
  const [donnees, setDonnees] = useState<SupplementsDuDossier | null>(null);
  const [message, setMessage] = useState<{ type: 'alert' | 'status'; texte: string } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [montant, setMontant] = useState('');
  const [quantite, setQuantite] = useState('1');
  const [motif, setMotif] = useState('');
  const [sansFacturer, setSansFacturer] = useState(false);

  const charger = useCallback(async () => {
    try {
      setDonnees(await source.lister());
    } catch (e) {
      setMessage({ type: 'alert', texte: messageDe(e) });
    }
  }, [source]);

  useEffect(() => {
    void charger();
  }, [charger]);

  async function agir(action: () => Promise<unknown>, reussite: string) {
    setEnCours(true);
    setMessage(null);
    try {
      await action();
      setMessage({ type: 'status', texte: reussite });
    } catch (e) {
      setMessage({ type: 'alert', texte: messageDe(e) });
    } finally {
      setEnCours(false);
      await charger();
    }
  }

  function ajouter(a: ArticleCatalogue) {
    const q = Number(quantite);
    if (!Number.isInteger(q) || q < 1 || q > 99) {
      setMessage({ type: 'alert', texte: 'La quantité doit être un nombre entier entre 1 et 99.' });
      return;
    }
    const nouveau: NouveauSupplement = { code: a.code, quantity: q, billable: !sansFacturer };
    if (a.unitAmountCents === null) {
      if (!montant.trim()) {
        setMessage({
          type: 'alert',
          texte: `Saisissez le montant de « ${a.label} » avant d’ajouter la ligne : son tarif n’est pas encore fixé.`,
        });
        return;
      }
      const c = centimesDe(montant);
      if (c === null) {
        setMessage({ type: 'alert', texte: 'Montant illisible : saisissez un montant en euros, par exemple 150 ou 150,50.' });
        return;
      }
      nouveau.unitAmountCents = c;
    }
    if (motif.trim()) nouveau.note = motif.trim();
    void agir(() => source.ajouter(nouveau), `« ${a.label} » ajouté au dossier.`);
  }

  return (
    <section aria-label="Suppléments en un clic" style={carte}>
      <h2 style={{ margin: 0, fontSize: 18, color: COULEURS.marine }}>Suppléments en un clic</h2>
      <span style={{ ...badge('#EEF0F8', COULEURS.marine), alignSelf: 'flex-start' }}>visible : équipe MAAT</span>
      <p style={discret}>
        Un clic ajoute la ligne au dossier. Tout est compté, même ce qui n’est pas facturé ; ce qui est facturable rejoint la prochaine
        facture par la règle « frais réels ».
      </p>
      {donnees && !donnees.fraisReelsActive ? (
        <p role="note" style={{ margin: 0, color: COULEURS.ambre, fontWeight: 600 }}>
          La règle « frais réels » n’est pas cochée dans la facturation de ce dossier : les suppléments sont comptés mais ne seront pas
          facturés tant qu’elle ne l’est pas.
        </p>
      ) : null}
      {donnees?.catalogueStatus === 'a_valider' ? (
        <p style={discret}>
          Tarifs à valider : la grille tarifaire du cabinet n’est pas encore reçue ; saisissez le montant des lignes sans tarif.
        </p>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="supplement-montant" style={{ fontWeight: 600, fontSize: 14 }}>
            Montant (€), si la ligne n’a pas de tarif
          </label>
          <input
            id="supplement-montant"
            inputMode="decimal"
            value={montant}
            disabled={!peutEcrire || enCours}
            onChange={(e) => setMontant(e.target.value)}
            style={champ}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="supplement-quantite" style={{ fontWeight: 600, fontSize: 14 }}>
            Quantité
          </label>
          <input
            id="supplement-quantite"
            inputMode="numeric"
            value={quantite}
            disabled={!peutEcrire || enCours}
            onChange={(e) => setQuantite(e.target.value)}
            style={champ}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="supplement-motif" style={{ fontWeight: 600, fontSize: 14 }}>
            Motif (facultatif)
          </label>
          <input
            id="supplement-motif"
            maxLength={200}
            value={motif}
            disabled={!peutEcrire || enCours}
            onChange={(e) => setMotif(e.target.value)}
            style={champ}
          />
        </div>
      </div>
      <label htmlFor="supplement-sans-facturer" style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: CIBLE_MIN }}>
        <input
          id="supplement-sans-facturer"
          type="checkbox"
          checked={sansFacturer}
          disabled={!peutEcrire || enCours}
          onChange={(e) => setSansFacturer(e.target.checked)}
        />
        Comptabiliser sans facturer
      </label>
      {!peutEcrire ? <p style={discret}>{RAISON}</p> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {(donnees?.catalogue ?? []).map((a) => (
          <button
            key={a.code}
            type="button"
            style={styleBouton}
            aria-label={`Ajouter ${a.label}`}
            disabled={!peutEcrire || enCours}
            title={peutEcrire ? undefined : RAISON}
            onClick={() => ajouter(a)}
          >
            {`${a.label} · ${a.unitAmountCents === null ? 'montant à saisir' : euros(a.unitAmountCents)}`}
          </button>
        ))}
      </div>
      {message ? (
        <p role={message.type} style={{ margin: 0, color: message.type === 'alert' ? COULEURS.rouge : COULEURS.marine, fontWeight: 600 }}>
          {message.texte}
        </p>
      ) : null}
      {donnees && donnees.supplements.length === 0 ? <p style={discret}>Aucune ligne supplémentaire sur ce dossier.</p> : null}
      {donnees && donnees.supplements.length > 0 ? (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table aria-label="Lignes supplémentaires" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
            <thead>
              <tr>
                {['Ligne', 'Montant', 'État'].map((t) => (
                  <th key={t} scope="col" style={{ textAlign: 'left', padding: '6px', color: COULEURS.ardoise }}>
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {donnees.supplements.map((s) => (
                <tr key={s.id}>
                  <td style={{ padding: '8px 6px', borderTop: '1px solid #EEEDF3', minWidth: 140, verticalAlign: 'top' }}>
                    {s.quantity > 1 ? `${s.label} (× ${s.quantity})` : s.label}
                    {s.note ? <span style={{ ...discret, display: 'block' }}>{s.note}</span> : null}
                    <button
                      type="button"
                      style={{ ...styleBouton, marginTop: 6 }}
                      aria-label={`Retirer ${s.label}`}
                      disabled={!peutEcrire || enCours || s.invoiced}
                      title={s.invoiced ? 'Déjà facturée : elle ne peut plus être retirée (avoir).' : peutEcrire ? undefined : RAISON}
                      onClick={() => void agir(() => source.retirer(s.id), `« ${s.label} » retiré.`)}
                    >
                      Retirer
                    </button>
                  </td>
                  <td style={{ padding: '8px 6px', borderTop: '1px solid #EEEDF3', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {euros(s.totalCents)}
                  </td>
                  <td style={{ padding: '8px 6px', borderTop: '1px solid #EEEDF3', verticalAlign: 'top' }}>
                    {s.invoiced ? (
                      <span style={badge('#E4F3EC', '#1F5E43')}>facturée</span>
                    ) : s.billable ? (
                      <span style={badge(COULEURS.ambreFond, COULEURS.ambre)}>à facturer</span>
                    ) : (
                      <span style={badge('#EEF0F8', COULEURS.marine)}>comptée, non facturée</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {donnees ? (
        <p style={{ margin: 0 }}>
          {`À facturer : ${euros(donnees.billableTotalCents)} · Compté sans facturation : ${euros(donnees.notBilledTotalCents)}`}
        </p>
      ) : null}
    </section>
  );
}
